import { useRef, useState } from "preact/hooks";
import { Loader2, Star } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { submitPdpRating } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import { FromSocialOcean } from "./FromSocialOcean";

type RatingValue = 1 | 2 | 3 | 4 | 5;
const RATINGS: RatingValue[] = [1, 2, 3, 4, 5];

const RATING_HINTS: Record<RatingValue, string> = {
  1: "I don't like it",
  2: "Not for me",
  3: "It's okay",
  4: "I like it",
  5: "I love it",
};

const POINTS_PER_RATING = 10;

// Review input is hidden for now — flip to true to collect written reviews again.
const SHOW_REVIEW_INPUT = false;

// Matches the ActionResultDialog "Post" button: that one is the shadcn <Button>
// (default size: h-9, centered, padded) PLUS PILL_FILLED_CLASS. This widget uses
// a native <button>, so we inline the Button's base sizing/layout (first line)
// together with the verbatim PILL_FILLED_CLASS pink-pill styling (second line).
const SUBMIT_BTN_CLASS =
  "tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:h-9 tw:px-2.5 tw:border tw:transition-all tw:cursor-pointer tw:select-none tw:outline-none " +
  "tw:w-full tw:rounded-md tw:bg-[#FAC8CA] tw:border-[#FAC8CA] tw:text-[#523D33] hover:tw:bg-[#FAC8CA]/80 disabled:tw:opacity-40 disabled:tw:cursor-not-allowed tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]";

export interface RateProductCardProps {
  shop: string;
  productId: number;
  title: string;
  questionId: number;
  shopifyOrderId: string;
  onSubmitted?: () => void;
}

export function RateProductCard({
  shop,
  productId,
  title,
  questionId,
  shopifyOrderId,
  onSubmitted,
}: RateProductCardProps) {
  const [selected, setSelected] = useState<RatingValue | null>(null);
  const [hovered, setHovered] = useState<RatingValue | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const hasTrackedSelection = useRef(false);

  const submitMutation = useMutation({
    mutationFn: (rating: RatingValue) =>
      submitPdpRating({
        shop,
        questionId,
        shopifyOrderId,
        productId,
        rating,
        comment: comment.trim() ? comment.trim() : null,
      }),
    onSuccess: (_void, rating) => {
      setError(false);
      setSubmitted(true);
      trackEvent("product_rating.submitted", { rating });
      onSubmitted?.();
    },
    onError: () => setError(true),
  });

  const pending = submitMutation.isPending;
  const disabled = submitted || pending;
  const hasSelection = selected != null;

  if (submitted) {
    return (
      <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:rounded-2xl tw:border tw:border-[#FAC8CA] tw:bg-white tw:px-5 tw:py-6 tw:mb-6">
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-5 tw:py-2 tw:animate-in tw:fade-in tw:duration-300">
          <span className="tw:text-7xl tw:leading-none" role="img" aria-label="gift">
            🎁
          </span>
          <p className="tw:m-0 tw:text-[2.05rem] tw:font-bold tw:text-[#fc5899]">
            +{POINTS_PER_RATING} points
          </p>
          <p className="tw:m-0 tw:max-w-xs tw:text-[1.02rem] tw:font-medium tw:leading-snug tw:text-[#1a1310]">
            Thank you for helping others discover products they&apos;ll love.
          </p>
        </div>
        <div className="tw:mt-4 tw:flex tw:justify-center">
          <FromSocialOcean className="tw:object-contain tw:block tw:w-24 tw:opacity-70" />
        </div>
      </div>
    );
  }

  return (
    <div className="tw:relative tw:flex tw:flex-col tw:items-center tw:text-center tw:rounded-2xl tw:border tw:border-[#FAC8CA] tw:bg-white tw:px-5 tw:py-6 tw:mb-6">
      {/* Title + stars cluster — the highlight sweep is scoped to ONLY this
          region, so it never runs over the review input or submit button. */}
      <div className="tw:relative tw:w-full tw:flex tw:flex-col tw:items-center">
        <h2 className="tw:m-0 tw:text-base tw:font-semibold tw:text-[#1a1310]">
        Enjoying {title}?
      </h2>

      {/* Stars sit directly beneath the heading, full size, never clipped. */}
      <div
        className="tw:mt-4 tw:flex tw:items-center tw:justify-center"
        role="radiogroup"
        aria-label="Rate product"
      >
        {RATINGS.map((n) => {
          const active = (hovered ?? selected ?? 0) >= n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected === n}
              aria-label={`Rate ${n} out of 5`}
              disabled={disabled}
              onMouseEnter={() => {
                if (!disabled) setHovered(n);
              }}
              onMouseLeave={() => {
                if (!disabled) setHovered(null);
              }}
              onFocus={() => {
                if (!disabled) setHovered(n);
              }}
              onBlur={() => {
                if (!disabled) setHovered(null);
              }}
              onClick={() => {
                if (!hasTrackedSelection.current) {
                  hasTrackedSelection.current = true;
                  trackEvent("product_rating.star_selected", { rating: n });
                }
                setSelected(n);
              }}
              className="tw:p-1 tw:transition-transform tw:disabled:cursor-default tw:disabled:pointer-events-none hover:tw:scale-110 active:tw:scale-95"
            >
              <Star
                strokeWidth={1.5}
                className="tw:size-10 tw:text-[#FAC8CA] tw:transition-colors tw:duration-150"
                fill={active ? "currentColor" : "none"}
              />
            </button>
          );
        })}
      </div>

      {/* Hint reserves space only at desktop widths, where hovering a star
          previews it. On mobile there's no hover, so the hint (and its space)
          only appear once a star is tapped — no dead gap before selection. */}
      <p
        className={`tw:mt-1 tw:overflow-hidden tw:text-sm tw:font-medium tw:text-muted-foreground tw:transition-opacity tw:duration-150 ${
          hasSelection ? "tw:h-5" : "tw:h-0 tw:md:h-5"
        }`}
      >
        {(() => {
          const r = hovered ?? selected;
          return r != null ? RATING_HINTS[r] : "";
        })()}
      </p>

      {/* Highlight sweep — clipped + non-interactive, scoped to the title +
          stars region above (not the input/submit below). */}
      <div
        aria-hidden="true"
        className="so-rate-flash tw:pointer-events-none tw:absolute tw:inset-0 tw:overflow-hidden"
      />
      </div>

      {hasSelection ? (
        <div className="tw:mt-2 tw:flex tw:w-full tw:max-w-xs tw:flex-col tw:items-stretch tw:gap-2 tw:animate-in tw:fade-in tw:duration-300">
          {SHOW_REVIEW_INPUT ? (
            <textarea
              value={comment}
              onInput={(e) => setComment((e.target as HTMLTextAreaElement).value)}
              disabled={disabled}
              rows={3}
              placeholder="Add a review (optional)"
              aria-label="Add a review (optional)"
              className="tw:w-full tw:resize-none tw:rounded-lg tw:border tw:border-[#FAC8CA]/60 tw:bg-white tw:p-3 tw:text-sm tw:text-[#1a1310] tw:outline-none focus:tw:border-[#FAC8CA]"
            />
          ) : null}

          {error ? (
            <p className="tw:m-0 tw:text-xs tw:text-[#b42318]">
              We couldn&apos;t save that. Try again.
            </p>
          ) : null}

          <button
            type="button"
            disabled={!hasSelection || pending}
            onClick={() => {
              if (selected != null) submitMutation.mutate(selected);
            }}
            className={SUBMIT_BTN_CLASS}
          >
            {pending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Submit"
            )}
          </button>
        </div>
      ) : null}

      <div className="tw:mt-4 tw:flex tw:justify-center">
        <FromSocialOcean className="tw:object-contain tw:block tw:w-24 tw:opacity-70" />
      </div>
    </div>
  );
}
